import { UsersConsts } from 'util/Constants';
import { WebAuthnKey } from 'client/MFA/webauthn/WebAuthnTypes';
import CollectionApi from 'client/api/v2/CollectionApi';
import Util from 'client/util/Util';
import axios from 'axios';

const { arrayBufferToString, stringToArrayBuffer } = Util;

export const getBaseUrl = (id = '') => `${UsersConsts.APIPaths.users}${id}/webauthn-credentials`;
export const getWebAuthKeyUrl = (id = '', webAuthnKeyId = '') => `${getBaseUrl(id)}/${webAuthnKeyId}`;

class SystemUserWebAuthnApi extends CollectionApi {
  constructor() {
    super(getBaseUrl(), {
      skip: 0,
      limit: 100,
      sort: 'create_date',
    });
  }

  /**
   * Fetch PublicKeyCredentialOptions from the server replacing base64 encoded strings with their
   * corresponding ArrayBuffer instances.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/PublicKeyCredentialCreationOptions/challenge
   * @see https://developer.mozilla.org/en-US/docs/Web/API/PublicKeyCredentialCreationOptions/user
   */
  startRegistration(id) {
    const url = `${UsersConsts.APIPaths.users}${id}/webauthn-credential/enroll`;

    return this
      .initialize()
      .then(() => axios.post(url, {}))
      .then((resp) => {
        const { token, publicKey } = resp.data;

        return {
          token,
          publicKey: {
            ...publicKey,
            challenge: stringToArrayBuffer(publicKey.challenge),
            user: {
              ...publicKey.user,
              id: stringToArrayBuffer(publicKey.user.id),
            },
          },
        };
      });
  }

  /**
   * Pass the PublicKeyCredentials to the server for validation and persistence replacing
   * ArrayBuffer values with their corresponding base64 encoded strings.
   */
  finishRegistration(id, token, name, publicKeyCredential) {
    const params = {
      token,
      name,
      credential: {
        id: publicKeyCredential.id,
        rawId: arrayBufferToString(publicKeyCredential.rawId),
        response: {
          attestationObject: arrayBufferToString(publicKeyCredential.response.attestationObject),
          clientDataJSON: arrayBufferToString(publicKeyCredential.response.clientDataJSON),
        },
        type: publicKeyCredential.type,
      },
    };

    return this
      .initialize()
      .then(() => axios.post(getBaseUrl(id), params))
      .then((resp) => {
        const webAuthnKey = new WebAuthnKey(resp.data);
        return webAuthnKey;
      });
  }

  fetchAll(id) {
    return this.getAllItems(getBaseUrl(id));
  }

  delete(id, webAuthnKey) {
    const url = getWebAuthKeyUrl(id, webAuthnKey.id);

    return this
      .initialize()
      .then(() => axios.delete(url, webAuthnKey));
  }

  update(id, webAuthnKey) {
    const url = getWebAuthKeyUrl(id, webAuthnKey.id);

    return this
      .initialize()
      .then(() => axios.patch(url, { name: webAuthnKey.name }));
  }
}

export default new SystemUserWebAuthnApi();
